import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Brain, TrendingUp, MapPin, Truck, AlertTriangle, MessageSquare,
  Activity, Loader2, RefreshCw, Send, Trash2, Sparkles, ArrowUpRight,
  ArrowDownRight, Cloud, Fuel, Anchor, Crown, Target, Zap, Shield, Wrench,
  Send as SendIcon, ChevronRight,
} from 'lucide-react';
import { HexAccent } from '../../../components/hive/HexMotif';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'market', label: 'Market Overview', icon: TrendingUp, color: '#D4AF37' },
  { id: 'load', label: 'Load Analysis', icon: Target, color: '#10B981' },
  { id: 'lanes', label: 'Lane Intelligence', icon: MapPin, color: '#3B82F6' },
  { id: 'carriers', label: 'Carrier Performance', icon: Truck, color: '#8B5CF6' },
  { id: 'alerts', label: 'Smart Alerts', icon: AlertTriangle, color: '#F59E0B' },
  { id: 'assistant', label: 'AI Assistant', icon: MessageSquare, color: '#EC4899' },
];

const HiveIntelligenceSection = () => {
  const [tab, setTab] = useState('market');

  return (
    <div className="space-y-6" data-testid="hive-intelligence-section">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0D0D0D] to-[#121212] border border-[#D4AF37]/30 p-5 sm:p-6">
        <HexAccent size={120} color="#D4AF37" className="absolute -top-6 -right-6 opacity-[0.08]" />
        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
            <Brain className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em]">
                AI · Powered by Claude Sonnet 4.5
              </span>
            </div>
            <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black text-white uppercase tracking-wide mb-1">
              Hive Intelligence
            </h2>
            <p className="text-white/60 text-sm">
              Freight market analytics, dispatch decisions, and operational intelligence — admin only.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 overflow-x-auto" data-testid="intel-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 sm:px-5 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                active ? 'border-current' : 'text-white/50 border-transparent hover:text-white/80'
              }`}
              style={active ? { color: t.color, borderColor: t.color } : {}}
              data-testid={`intel-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {tab === 'market' && <MarketOverview />}
      {tab === 'load' && <LoadAnalysis />}
      {tab === 'lanes' && <LaneIntelligence />}
      {tab === 'carriers' && <CarrierPerformance />}
      {tab === 'alerts' && <SmartAlerts />}
      {tab === 'assistant' && <DispatchAssistant />}
    </div>
  );
};

// ─────────── Market Overview ───────────
const MarketOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/intel/market-overview${force ? '?force_refresh=true' : ''}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetch(false); }, []);

  if (loading) return <LoadingState message="Generating market intelligence..." />;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="market-overview">
      {/* Headline + Refresh */}
      <div className="bg-[#121212] border border-white/10 p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2">Today's Briefing</p>
            <h3 className="font-['Barlow_Condensed'] text-xl sm:text-2xl font-bold text-white leading-tight mb-2 break-words">
              {data.headline}
            </h3>
            <p className="text-white/70 leading-relaxed text-sm sm:text-base">{data.summary}</p>
          </div>
          <button
            onClick={() => fetch(true)}
            disabled={refreshing}
            className="p-2 hover:bg-white/10 transition-colors flex-shrink-0"
            title="Refresh"
            data-testid="market-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 text-white/60 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {data.cache_age_minutes !== undefined && (
          <p className="text-white/30 text-[10px] uppercase tracking-wider">
            Updated {data.cache_age_minutes} min ago · Auto-refresh every 6h
          </p>
        )}
      </div>

      {/* Hot Lanes + Diesel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#D4AF37]" />
            Hot Lanes
          </h4>
          <div className="space-y-2">
            {(data.hot_lanes || []).map((l, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-black/40 border border-white/5 hover:border-[#D4AF37]/30 transition-colors gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">
                    {l.origin} → {l.destination}
                  </p>
                  <p className="text-white/50 text-xs">{l.equipment}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold text-sm">{l.rpm}</span>
                  {l.trend === 'rising' ? (
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          {/* Diesel */}
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/50 text-xs uppercase tracking-wider">Diesel Avg</span>
              <Fuel className="w-4 h-4 text-[#F59E0B]" />
            </div>
            <p className="text-3xl font-black text-white mb-1">{data.diesel_average}</p>
            <p className="text-white/40 text-xs uppercase tracking-wider">Trend: {data.diesel_trend}</p>
          </div>

          {/* Ports */}
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Anchor className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-white text-sm font-bold uppercase tracking-wider">Ports</span>
            </div>
            <div className="space-y-2">
              {(data.ports_status || []).map((p, i) => (
                <div key={i} className="text-xs">
                  <p className="text-white">{p.port}</p>
                  <p className="text-white/50 truncate">{p.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Regional Demand */}
      <div className="bg-[#121212] border border-white/10 p-5">
        <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          Regional Demand
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {(data.regional_demand || []).map((r, i) => (
            <div key={i} className="bg-black/40 border border-white/5 p-3">
              <p className="text-white font-medium text-sm mb-1">{r.region}</p>
              <p className={`text-xs font-bold uppercase mb-1 ${
                r.demand === 'high' ? 'text-green-500' : r.demand === 'low' ? 'text-red-400' : 'text-[#D4AF37]'
              }`}>{r.demand}</p>
              <p className="text-white/50 text-xs leading-snug">{r.note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts strip */}
      {data.alerts?.length > 0 && (
        <div className="bg-[#121212] border border-[#F59E0B]/30 p-5">
          <h4 className="text-[#F59E0B] font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Active Disruptions
          </h4>
          <div className="space-y-2">
            {data.alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-black/40 border border-white/5">
                <Cloud className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{a.message}</p>
                  <p className="text-white/40 text-xs uppercase tracking-wider mt-1">{a.type} · {a.severity}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────── Load Analysis ───────────
const LoadAnalysis = () => {
  const [form, setForm] = useState({ origin: '', destination: '', equipment: 'Dry Van', rate: '', miles: '', notes: '' });
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/intel/load-history?limit=5`).then(r => setHistory(r.data)).catch(() => {});
  }, [result]);

  const analyze = async () => {
    if (!form.origin || !form.destination || !form.rate) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/intel/analyze-load`, {
        origin: form.origin,
        destination: form.destination,
        equipment: form.equipment,
        rate: parseFloat(form.rate),
        miles: form.miles ? parseFloat(form.miles) : null,
        notes: form.notes,
      });
      setResult(r.data);
    } catch (e) {
      console.error(e);
      alert('Analysis failed. Check console.');
    } finally { setAnalyzing(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="load-analysis">
      {/* Form */}
      <div className="lg:col-span-1 bg-[#121212] border border-white/10 p-5 space-y-3 self-start">
        <h4 className="text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2">
          <Target className="w-4 h-4 text-[#10B981]" />
          Analyze a Load
        </h4>
        <Input label="Origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} placeholder="Atlanta, GA" testid="load-origin" />
        <Input label="Destination" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} placeholder="Chicago, IL" testid="load-destination" />
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Equipment</label>
          <select
            value={form.equipment}
            onChange={(e) => setForm({ ...form, equipment: e.target.value })}
            className="w-full bg-black/40 border border-white/10 text-white p-2.5 focus:border-[#10B981] focus:outline-none"
            data-testid="load-equipment"
          >
            <option>Dry Van</option>
            <option>Reefer</option>
            <option>Flatbed</option>
            <option>Power Only</option>
            <option>Step Deck</option>
            <option>Tanker</option>
          </select>
        </div>
        <Input label="Total Rate ($)" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} placeholder="2400" type="number" testid="load-rate" />
        <Input label="Miles (optional)" value={form.miles} onChange={(v) => setForm({ ...form, miles: v })} placeholder="720" type="number" testid="load-miles" />
        <Input label="Notes (optional)" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Detention paid after 2hrs" testid="load-notes" />
        <button
          onClick={analyze}
          disabled={analyzing || !form.origin || !form.destination || !form.rate}
          className="w-full py-3 bg-[#10B981] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#34D399] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          data-testid="analyze-load-btn"
        >
          {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <><Sparkles className="w-4 h-4" /> Run AI Analysis</>}
        </button>
      </div>

      {/* Results */}
      <div className="lg:col-span-2 space-y-4">
        {analyzing && <LoadingState message="AI analyzing load profitability..." />}
        {!analyzing && !result && (
          <div className="bg-[#121212] border border-white/10 p-8 text-center">
            <Target className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">Enter load details on the left, get AI-powered profitability analysis here.</p>
            {history.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10 text-left">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Recent Analyses</p>
                <div className="space-y-2">
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 hover:bg-white/5">
                      <span className="text-white/70 truncate">{h.origin} → {h.destination}</span>
                      <RatingPill rating={h.analysis?.profitability_rating} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {result && <LoadResult result={result.analysis} />}
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, placeholder, type = 'text', testid }) => (
  <div>
    <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-black/40 border border-white/10 text-white p-2.5 focus:border-[#10B981] focus:outline-none"
      data-testid={testid}
    />
  </div>
);

const RatingPill = ({ rating }) => {
  const colors = {
    'Excellent': '#10B981', 'Good': '#22C55E', 'Average': '#D4AF37',
    'Below Average': '#F59E0B', 'Poor': '#EF4444',
  };
  const c = colors[rating] || '#888';
  return (
    <span className="text-xs font-bold uppercase px-2 py-0.5" style={{ color: c, backgroundColor: `${c}15`, border: `1px solid ${c}40` }}>
      {rating || '—'}
    </span>
  );
};

const LoadResult = ({ result }) => {
  const recColors = { 'ACCEPT': '#10B981', 'NEGOTIATE': '#D4AF37', 'PASS': '#EF4444' };
  const recColor = recColors[result.recommendation] || '#888';
  return (
    <div className="space-y-4">
      {/* Recommendation banner */}
      <div className="bg-[#121212] border-2 p-5" style={{ borderColor: recColor }}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${recColor}15`, border: `1px solid ${recColor}40` }}>
            <Crown className="w-6 h-6" style={{ color: recColor }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: recColor }}>
              Recommendation · Confidence {result.confidence_score}%
            </p>
            <h3 className="font-['Barlow_Condensed'] text-3xl font-black uppercase mb-2" style={{ color: recColor }}>
              {result.recommendation}
            </h3>
            <p className="text-white/80 text-sm leading-relaxed">{result.recommendation_reason}</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiTile label="RPM" value={result.estimated_rpm} sub={`Mkt avg ${result.lane_market_avg_rpm}`} />
        <KpiTile label="Net Profit" value={result.estimated_net_profit} accent />
        <KpiTile label="Fuel Est." value={result.fuel_cost_estimate} />
        <KpiTile label="Profit" rating={result.profitability_rating} />
      </div>

      {/* Negotiation */}
      <div className="bg-[#121212] border border-white/10 p-5">
        <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3">Negotiation Range</h4>
        <div className="grid grid-cols-3 gap-3 text-center">
          <NegoTile label="Floor" value={result.negotiation_floor} muted />
          <NegoTile label="Target" value={result.negotiation_target} accent />
          <NegoTile label="Ceiling" value={result.negotiation_ceiling} muted />
        </div>
      </div>

      {/* Reload */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-2">Deadhead Risk</h4>
          <p className={`text-2xl font-black mb-1 ${
            result.deadhead_risk === 'Low' ? 'text-green-500' : result.deadhead_risk === 'High' ? 'text-red-400' : 'text-[#D4AF37]'
          }`}>{result.deadhead_risk}</p>
          <p className="text-white/60 text-xs">{result.deadhead_explanation}</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-2">Reload Probability</h4>
          <p className="text-2xl font-black text-[#D4AF37] mb-2">{result.reload_probability}</p>
          <div className="flex flex-wrap gap-1">
            {(result.reload_markets || []).map((m, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">{m}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Risks/Opps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ListPanel title="Risks" color="#EF4444" items={result.risks || []} />
        <ListPanel title="Opportunities" color="#10B981" items={result.opportunities || []} />
      </div>
    </div>
  );
};

const KpiTile = ({ label, value, sub, accent, rating }) => (
  <div className="bg-[#121212] border border-white/10 p-3">
    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</p>
    {rating ? <RatingPill rating={rating} /> : (
      <p className={`text-lg font-black ${accent ? 'text-[#10B981]' : 'text-white'}`}>{value}</p>
    )}
    {sub && <p className="text-white/30 text-[10px] mt-1">{sub}</p>}
  </div>
);

const NegoTile = ({ label, value, muted, accent }) => (
  <div className={`p-3 ${accent ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/40' : 'bg-black/40 border border-white/5'}`}>
    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-lg font-black ${accent ? 'text-[#D4AF37]' : muted ? 'text-white/60' : 'text-white'}`}>{value}</p>
  </div>
);

const ListPanel = ({ title, color, items }) => (
  <div className="bg-[#121212] border border-white/10 p-5">
    <h4 className="font-bold uppercase tracking-wider text-sm mb-3" style={{ color }}>{title}</h4>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="text-white/70 text-sm flex items-start gap-2">
          <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ─────────── Lane Intelligence ───────────
const LaneIntelligence = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/intel/lanes`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState message="Computing lane analytics..." />;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="lane-intelligence">
      {data.seasonal_insight && (
        <div className="bg-[#121212] border-l-2 border-[#3B82F6] p-4">
          <p className="text-[#3B82F6] text-xs font-bold uppercase tracking-widest mb-1">Seasonal Insight</p>
          <p className="text-white/80 text-sm">{data.seasonal_insight}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top */}
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-green-500" /> Top Performing Lanes
          </h4>
          <div className="space-y-2">
            {(data.top_performing_lanes || []).map((l, i) => (
              <LaneRow key={i} lane={l} positive />
            ))}
          </div>
        </div>
        {/* Weak */}
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
            <ArrowDownRight className="w-4 h-4 text-red-400" /> Weakest Lanes
          </h4>
          <div className="space-y-2">
            {(data.weakest_lanes || []).map((l, i) => (
              <LaneRow key={i} lane={l} />
            ))}
          </div>
        </div>
      </div>

      {/* Regional RPM table */}
      <div className="bg-[#121212] border border-white/10 p-5 overflow-x-auto">
        <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3">Regional RPM Benchmarks</h4>
        <table className="w-full min-w-[480px]">
          <thead><tr className="border-b border-white/10 text-white/50 text-xs uppercase tracking-wider">
            <th className="text-left py-2">Region</th>
            <th className="text-right py-2">Dry Van</th>
            <th className="text-right py-2">Reefer</th>
            <th className="text-right py-2">Flatbed</th>
          </tr></thead>
          <tbody>
            {(data.regional_avg_rpm || []).map((r, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="py-3 text-white">{r.region}</td>
                <td className="py-3 text-right text-[#D4AF37] font-bold">{r.dry_van}</td>
                <td className="py-3 text-right text-[#D4AF37] font-bold">{r.reefer}</td>
                <td className="py-3 text-right text-[#D4AF37] font-bold">{r.flatbed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Heatmap */}
      <div className="bg-[#121212] border border-white/10 p-5">
        <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4">Demand Heatmap</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {(data.demand_heatmap || []).map((h, i) => {
            const intensity = h.intensity || 0;
            const opacity = 0.15 + (intensity / 100) * 0.6;
            return (
              <div
                key={i}
                className="p-3 border border-[#D4AF37]/30"
                style={{ backgroundColor: `rgba(212, 175, 55, ${opacity})` }}
              >
                <p className="text-white font-medium text-sm">{h.region}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white/80 text-xs">{intensity}/100</span>
                  {h.trend === 'rising' ? (
                    <ArrowUpRight className="w-3 h-3 text-green-500" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 text-red-400" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LaneRow = ({ lane, positive }) => (
  <div className="flex items-center justify-between p-2.5 bg-black/40 border border-white/5">
    <div className="min-w-0 flex-1">
      <p className="text-white text-sm truncate">{lane.lane}</p>
      <p className="text-white/40 text-xs">{lane.equipment} · {lane.volume} volume</p>
    </div>
    <div className="text-right flex-shrink-0 ml-2">
      <p className={`font-bold ${positive ? 'text-green-500' : 'text-red-400'}`}>{lane.avg_rpm}</p>
      <p className="text-white/40 text-[10px]">Score {lane.score}</p>
    </div>
  </div>
);

// ─────────── Carrier Performance ───────────
const CarrierPerformance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/intel/carriers`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState message="Analyzing carrier performance..." />;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="carrier-performance">
      <div className="bg-[#121212] border border-white/10 p-5">
        <p className="text-[#8B5CF6] text-xs font-bold uppercase tracking-widest mb-2">Fleet Overview</p>
        <p className="text-white/80 text-sm sm:text-base leading-relaxed">{data.summary}</p>
      </div>

      {data.rankings?.length > 0 && (
        <div className="bg-[#121212] border border-white/10 overflow-x-auto">
          <div className="p-4 border-b border-white/10">
            <h4 className="text-white font-bold uppercase tracking-wider text-sm">Carrier Rankings</h4>
          </div>
          <table className="w-full min-w-[640px]">
            <thead><tr className="border-b border-white/5 text-white/50 text-xs uppercase">
              <th className="text-left py-3 px-4">#</th>
              <th className="text-left py-3 px-4">Carrier</th>
              <th className="text-left py-3 px-4">Tier</th>
              <th className="text-right py-3 px-4">Score</th>
              <th className="text-left py-3 px-4">Note</th>
            </tr></thead>
            <tbody>
              {data.rankings.map((r, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-3 px-4 text-white/40 text-sm">{i + 1}</td>
                  <td className="py-3 px-4">
                    <p className="text-white text-sm">{r.company}</p>
                    <p className="text-white/40 text-xs">{r.email}</p>
                  </td>
                  <td className="py-3 px-4 text-[#D4AF37] uppercase text-xs font-bold">{r.tier}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-black ${r.score >= 80 ? 'text-green-500' : r.score >= 60 ? 'text-[#D4AF37]' : 'text-red-400'}`}>
                      {r.score}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white/60 text-xs">{r.strength} · {r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3">Insights</h4>
          <div className="space-y-3">
            {(data.insights || []).map((ins, i) => (
              <div key={i} className="border-l-2 border-[#8B5CF6] pl-3">
                <p className="text-white text-sm font-medium">{ins.title}</p>
                <p className="text-white/50 text-xs mt-1">{ins.detail}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-5">
          <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-3">Recommendations</h4>
          <ul className="space-y-2">
            {(data.recommendations || []).map((rec, i) => (
              <li key={i} className="text-white/70 text-sm flex items-start gap-2">
                <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0 text-[#10B981]" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ─────────── Smart Alerts ───────────
const SmartAlerts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/intel/alerts${force ? '?force_refresh=true' : ''}`);
      setData(r.data);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetch(false); }, []);

  if (loading) return <LoadingState message="Scanning for operational alerts..." />;
  if (!data) return null;

  const iconMap = {
    AlertTriangle, TrendingDown: ArrowDownRight, TrendingUp: ArrowUpRight, Cloud, Fuel, Wrench, Shield,
  };
  const sevColors = {
    info: '#3B82F6', warning: '#F59E0B', critical: '#EF4444',
  };

  return (
    <div className="space-y-4" data-testid="smart-alerts">
      <div className="flex items-center justify-between">
        <p className="text-white/60 text-sm">{data.alerts?.length || 0} active alerts · Auto-refresh every 4h</p>
        <button
          onClick={() => fetch(true)}
          disabled={refreshing}
          className="p-2 hover:bg-white/10 transition-colors"
          data-testid="alerts-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 text-white/60 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-3">
        {(data.alerts || []).map((a, i) => {
          const Icon = iconMap[a.icon] || AlertTriangle;
          const color = sevColors[a.severity] || '#888';
          return (
            <div
              key={i}
              className="bg-[#121212] border-l-2 p-4 flex items-start gap-3"
              style={{ borderLeftColor: color }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
              >
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h5 className="text-white font-bold text-sm">{a.title}</h5>
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 flex-shrink-0"
                    style={{ color, backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
                  >
                    {a.severity}
                  </span>
                </div>
                <p className="text-white/70 text-sm mb-2">{a.message}</p>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-[#D4AF37] font-medium uppercase tracking-wider">→ {a.action}</span>
                  <span className="text-white/40">{a.affected_region}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────── Dispatch Assistant ───────────
const SUGGESTED_PROMPTS = [
  "What's the best reload market near Dallas today?",
  "Is an Atlanta to Chicago dry van lane profitable at $2.10/mi?",
  "Which regions are strongest for reefer this week?",
  "Best practices for negotiating detention pay?",
];

const DispatchAssistant = () => {
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('intel_chat_session');
    if (stored) return stored;
    const fresh = `intel-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem('intel_chat_session', fresh);
    return fresh;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/intel/assistant/history/${sessionId}`).then(r => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setSending(true);
    const optimistic = [...messages, { role: 'user', content: msg, created_at: new Date().toISOString() }];
    setMessages(optimistic);
    setInput('');
    try {
      const r = await axios.post(`${API}/admin/intel/assistant`, { session_id: sessionId, message: msg });
      setMessages([...optimistic, { role: 'assistant', content: r.data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      setMessages([...optimistic, { role: 'assistant', content: '⚠️ Sorry, I had a hiccup. Try again.', created_at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const clear = async () => {
    if (!window.confirm('Clear this conversation?')) return;
    await axios.delete(`${API}/admin/intel/assistant/history/${sessionId}`);
    setMessages([]);
  };

  return (
    <div className="bg-[#121212] border border-white/10 flex flex-col" style={{ height: '70vh', minHeight: '500px' }} data-testid="dispatch-assistant">
      <div className="p-4 border-b border-white/10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 bg-[#EC4899]/10 border border-[#EC4899]/40 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div className="min-w-0">
            <h4 className="text-white font-bold text-sm">Dispatch Assistant</h4>
            <p className="text-white/40 text-xs truncate">Ask anything about freight, lanes, carriers, rates</p>
          </div>
        </div>
        <button onClick={clear} className="p-2 hover:bg-white/10" title="Clear chat" data-testid="clear-chat-btn">
          <Trash2 className="w-4 h-4 text-white/50" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-14 h-14 bg-[#EC4899]/10 border border-[#EC4899]/40 flex items-center justify-center mb-3">
              <Sparkles className="w-7 h-7 text-[#EC4899]" />
            </div>
            <p className="text-white font-medium mb-1">Ready when you are.</p>
            <p className="text-white/50 text-sm mb-6 max-w-md">
              Ask about lanes, rates, reload markets, broker patterns, or any operational decision.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-2xl">
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => send(p)}
                  className="text-left p-3 bg-black/40 border border-white/10 hover:border-[#EC4899]/50 transition-colors text-white/70 text-xs"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] sm:max-w-[75%] p-3 ${
                m.role === 'user'
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-black/40 border border-white/10 text-white'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-black/40 border border-white/10 p-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#EC4899]" />
              <span className="text-white/60 text-sm">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/10 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Ask the Hive Intelligence..."
          disabled={sending}
          className="flex-1 bg-black/40 border border-white/10 text-white p-2.5 focus:border-[#EC4899] focus:outline-none text-sm"
          data-testid="assistant-input"
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="px-4 bg-[#EC4899] text-white hover:bg-[#F472B6] disabled:opacity-40 transition-colors"
          data-testid="assistant-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─────────── Shared ───────────
const LoadingState = ({ message }) => (
  <div className="bg-[#121212] border border-white/10 p-12 text-center">
    <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto mb-3" />
    <p className="text-white/60 text-sm">{message}</p>
  </div>
);

export default HiveIntelligenceSection;
